"use client";

import { FC, useState, useEffect, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { componentNameConstants, dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { handleRedirection } from "../../utils/helper";

interface Tab {
  id: string;
  label: string;
  content: ReactNode;
  url?: string;  
  target?: string; 
}

interface PageContentTabsProps {
  tabs: Tab[];
  defaultTab?: string;
  color?: string;
  currentTab?: string;
  lowerEnvParam?: string;
}

const PageContentTabs: FC<PageContentTabsProps> = ({ tabs, defaultTab, color, currentTab, lowerEnvParam = "" }) => {
  const router = useRouter();

  const [activeTab, setActiveTab] = useState(
    currentTab || defaultTab || tabs[0]?.id
  );

  const handleTabChange = (tab: Tab) => {
    const pageName = document.title.toLowerCase().includes("mtf");

    const clevertapObj = {
			EP_PAGE_NAME: pageName ? dlElementConstants.MTF_CLP : dlElementConstants.TRADING,
			EP_JOURNEY_NAME: pageName ? dlElementConstants.MTF_CLP_EP : dlElementConstants.TRADING_MALL_CLP_EP,
			EP_TAB: tab.label.toUpperCase(),
		};

		const dataLayerObj = {
			event: dlEventConstants.CLICK,
			event_type: dlEventConstants.TAB_CLICK,
			element: pageName ? dlElementConstants.MTF_TAB_CLICK : dlElementConstants.TRADING_TAB_CLICK,
			component: pageName ? componentNameConstants.MTF : componentNameConstants.TRADING,
			section_title: tab.label,
			cta_text: tab.label,
			destination_url: tab.url ? tab.url : `${window.location.pathname}`,
		};

    dataLayer("", dataLayerObj, clevertapObj);

    if (tab.url) { 
      const inNewTab = tab.target === "_blank" ? "true" : "false"; 
      handleRedirection(tab.url, router, inNewTab); 
      return; 
    }

    // Normal tab switching
    setActiveTab(tab.id);

    // Start from all existing query params in the URL
    const params = new URLSearchParams(window.location.search ?? "");

    if (lowerEnvParam && lowerEnvParam.trim() !== "") {
      const lowerEnvParams = new URLSearchParams(lowerEnvParam);
      lowerEnvParams.forEach((value, key) => {
        params.set(key, value);
      });
    }

    params.set("tab", tab.id);

    router.replace(`?${params.toString()}`, { scroll: false });
  };


  // Global Swiper update when tab changes
  useEffect(() => {
    const updateAllSwipers = () => {
      // Find all Swiper instances in the DOM
      const swiperElements = document.querySelectorAll('.swiper');
      
      swiperElements.forEach((swiperEl: any) => {
        if (swiperEl?.swiper) {
          const swiper = swiperEl.swiper;
          
          // Destroy and reinitialize navigation to reconnect refs
          if (swiper.navigation) {
            swiper.navigation.destroy();
            swiper.navigation.init();
            swiper.navigation.update();
          }
          
          // Update Swiper dimensions and layout
          swiper.update();
          swiper.updateSize();
          swiper.updateSlides();
          swiper.updateProgress();
          swiper.updateSlidesClasses();
          
          // Update pagination if it exists
          if (swiper.pagination) {
            swiper.pagination.init();
            swiper.pagination.render();
            swiper.pagination.update();
          }
          
          // Trigger slide change to update navigation states
          swiper.emit('slideChange');
        }
      });
    };

    // Delay to ensure DOM is fully updated after tab switch
    const timer = setTimeout(() => {
      updateAllSwipers();
    }, 150);

    return () => clearTimeout(timer);
  }, [activeTab]);

  return (
    <div className="w-full">
      {/* Tabs Header */}
      <div className="no-scrollbar w-full overflow-x-scroll border-b border-gray-300">
        <div className="flex h-[40px] items-center text-[12px]  md:h-auto max-sm:font-[500] pl-[16px] md:pt-[12px] md:pb-[12px] md:text-base md:leading-normal">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const isRedirectTab = !!tab.url;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab)}
                className="relative mr-3 cursor-pointer whitespace-nowrap px-[6px] pb-[1px] pt-[1px] md:mr-4"
              >
                <p className={isActive && !isRedirectTab ? "text-[#ff6700]" : (color ?? "text-[#002953]")}>
                  {tab.label}
                </p>
                {isActive && !isRedirectTab && (
                  <span className="absolute bottom-[-9px] left-0 h-[2px] w-full rounded-full bg-[#ff6a00] md:bottom-[-13px] md:h-[3px]"></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="w-full relative">
        {tabs.map((tab) => {
          // Don't render content for redirect tabs
          if (tab.url) return null;

          const isActive = activeTab === tab.id;

          return (
            <div 
              key={tab.id} 
              className={isActive ? "" : "absolute top-0 left-0 w-full"}
              style={isActive ? {} : { 
                visibility: 'hidden', 
                pointerEvents: 'none',
                height: 0,
                overflow: 'hidden'
              }}
            >
              {tab.content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PageContentTabs;